import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { LinearProgress, Typography } from '@material-ui/core';
import { formatMoney } from '../../helpers/invoiceHelpers';
import { formatPercent } from './refundFormat';
import { keys } from './constants';

// MUI v1's LinearProgress has no error colour, so the over-budget bar is a barColorPrimary
// classes override (the same slot-override idiom StatusChip uses).
const styles = ({ spacing: { unit }, palette }) => ({
  figures: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: `${unit * 2}px`,
  },
  figure: {
    minWidth: 0,
  },
  label: {
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  value: {
    fontVariantNumeric: 'tabular-nums',
  },
  valueError: {
    color: palette.error.main,
  },
  bar: {
    height: 8,
    borderRadius: 4,
    marginTop: unit,
    marginBottom: unit,
  },
  barError: {
    backgroundColor: palette.error.main,
  },
  caption: {
    display: 'block',
  },
  captionError: {
    color: palette.error.main,
  },
  rateErrors: {
    marginTop: unit,
  },
});

const BudgetMeter = ({
  classes,
  translate,
  refundableAmount,
  selectedTotal,
  remaining,
  isOverBudget,
  overRates,
  locale,
  currency,
}) => {
  const money = value => formatMoney(value, locale, currency);
  const pct = refundableAmount > 0 ? Math.min(100, (selectedTotal / refundableAmount) * 100) : 0;
  const hasRateError = overRates && overRates.length > 0;

  const figure = (labelKey, value, error) => (
    <div className={classes.figure}>
      <Typography variant="caption" color="textSecondary" className={classes.label}>
        {translate(labelKey)}
      </Typography>
      <Typography variant="title" className={`${classes.value} ${error ? classes.valueError : ''}`} noWrap>
        {value}
      </Typography>
    </div>
  );

  return (
    <div>
      <div className={classes.figures}>
        {figure(keys.meterRefundable, money(refundableAmount))}
        {figure(keys.meterSelected, money(selectedTotal))}
        <div className={classes.figure} aria-live="polite">
          {figure(keys.meterRemaining, money(isOverBudget ? 0 : remaining), isOverBudget)}
        </div>
      </div>
      <LinearProgress
        variant="determinate"
        value={pct}
        color="primary"
        className={classes.bar}
        classes={isOverBudget ? { barColorPrimary: classes.barError } : {}}
      />
      <Typography
        variant="caption"
        color={isOverBudget ? 'error' : 'textSecondary'}
        className={`${classes.caption} ${isOverBudget ? classes.captionError : ''}`}
      >
        {isOverBudget
          ? translate(keys.meterOver, { amount: money(selectedTotal - refundableAmount) })
          : translate(keys.meterHint)}
      </Typography>
      {hasRateError && (
        <div className={classes.rateErrors} aria-live="polite">
          {overRates.map(({ rate, used, cap }) => (
            <Typography key={rate} variant="caption" className={classes.captionError}>
              {translate(keys.meterOverRate, { rate: formatPercent(rate), selected: money(used), cap: money(cap) })}
            </Typography>
          ))}
        </div>
      )}
    </div>
  );
};

BudgetMeter.propTypes = {
  classes: PropTypes.shape({}),
  translate: PropTypes.func.isRequired,
  refundableAmount: PropTypes.number,
  selectedTotal: PropTypes.number,
  remaining: PropTypes.number,
  isOverBudget: PropTypes.bool,
  overRates: PropTypes.arrayOf(PropTypes.shape({})),
  locale: PropTypes.string,
  currency: PropTypes.string,
};

export const BudgetMeterTest = BudgetMeter;

export default withStyles(styles)(BudgetMeter);
